import { Component, HostBinding, Inject, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../../shared/input/input.component';
import { confirmSignIn } from 'aws-amplify/auth';
import { AccountService } from '../../model/account/account.service';
import { PermissionService } from '../../permissions.service';
import { EventBusService } from '../../event-bus.service';
import { PasswordRequirementsComponent } from '../../shared/password-requirements/password-requirements.component';
import { passwordRequirementsValidator } from '../../utilities/passwordRequirements';
import { directionForLocale } from '../../utilities/localeDirection';

@Component({
  selector: 'app-new-password',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ToastModule,
    InputComponent,
    PasswordRequirementsComponent,
  ],
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss'],
})
export class NewPasswordComponent {
  newPasswordForm: FormGroup;
  @HostBinding('attr.dir') get direction(): 'rtl' | 'ltr' {
    return directionForLocale(this.locale);
  }

  get password(): string {
    return this.newPasswordForm.get('password')?.value ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private accountService: AccountService,
    private permissionService: PermissionService,
    private eventBusService: EventBusService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.newPasswordForm = this.fb.group({
      password: [null, [Validators.required, passwordRequirementsValidator()]],
      confirmPassword: [null, [Validators.required]],
    });
  }

  async submitNewPassword(): Promise<void> {
    const { password, confirmPassword } = this.newPasswordForm.value;

    if (!password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@newPassword.mismatchSummary:Password Mismatch`,
        detail: $localize`:@@newPassword.mismatchDetail:Password and confirm password must match.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
      return;
    }

    try {
      const response = await confirmSignIn({
        challengeResponse: password,
      });

      if (response.isSignedIn) {
        const account = await this.accountService.fetch();
        this.permissionService.reloadRolesAndPermissions();
        this.eventBusService.publish('user:login', account.email);
        this.router.navigate(['/']);
        return;
      }

      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@newPassword.unableToSignInSummary:Unable To Complete Sign In`,
        detail: $localize`:@@newPassword.unableToSignInDetail:Additional authentication steps are required. Please try signing in again.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
      this.router.navigate(['/login']);
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@newPassword.updateFailedSummary:Update Password Failed`,
        detail: error?.message || $localize`:@@newPassword.updateFailedDetail:Could not update password. Please try again.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
    }
  }
}
